import { LanguageSupport, StreamLanguage, StreamParser } from '@codemirror/language';

import {
  ALL_CONSTANTS,
  ALL_FUNCTIONS,
  DEFAULT_FORMULA_EDITOR_CONFIG,
  expandCompositeOperators,
  FormulaEditorConfig,
  getEffectiveConstants,
  getEffectiveFunctions,
  getEffectiveOperators,
  getInConditionalOnlyOperators
} from '../utils/formula-editor-config';

/** All known math function names (used for recognition regardless of restrictions) */
const ALL_FUNCTIONS_SET = new Set(ALL_FUNCTIONS);

/** All known math constant names (used for recognition regardless of restrictions) */
const ALL_CONSTANTS_SET = new Set(ALL_CONSTANTS);

/** Logical / keyword operators that are written as words */
const WORD_OPERATORS = new Set(['and', 'or', 'xor', 'not', 'to', 'in']);

/**
 * A minimal StreamParser for mathematical formula syntax.
 * Recognizes:
 *  - Numbers (integers and decimals, scientific notation)
 *  - Operators: +, -, *, /, ^, %, >, <, >=, <=, ==, !=, ~, <<, >>, >>>
 *  - Word operators: and, or, xor, not, to, in
 *  - Marks `&` / `&&` / `|` / `||` as `invalid` — users must type `and` / `or` instead
 *  - Math functions: sqrt, sin, cos, abs, etc.
 *  - Math constants: pi, e, Infinity, etc. (distinct highlighting)
 *  - Variable names: alphanumeric + hyphens (e.g. my-var-name)
 *  - Marks disallowed operators/functions/constants as errors
 */
export function formulaStreamParser(
  variableNames: Set<string>,
  config: FormulaEditorConfig = DEFAULT_FORMULA_EDITOR_CONFIG
): StreamParser<Record<string, never>> {
  const allowedOperators = getEffectiveOperators(config);
  // Expand composite tokens like '?:' so that '?' and ':' are individually recognised
  const expandedAllowedOperators = expandCompositeOperators(allowedOperators);
  const allowedFunctions = getEffectiveFunctions(config);
  const allowedConstants = getEffectiveConstants(config);
  const inConditionalOnlyOperators = getInConditionalOnlyOperators(config);

  return {
    token(stream): string | null {
      // Skip whitespace
      if (stream.eatSpace()) {
        return null;
      }

      // Numbers (integers, decimals, scientific notation e.g. 1e10, 2.5E-3)
      if (stream.match(/^\d+(\.\d+)?([eE][+-]?\d+)?/)) {
        return 'number';
      }

      // Triple-character operators (>>>)
      if (stream.match(/^>>>/)) {
        if (config.restrictOperators && !expandedAllowedOperators.has('>>>')) {
          return 'error disallowedOperator';
        }
        return 'operator';
      }

      // & or && → invalid (use 'and' keyword instead)
      if (stream.match(/^&&?/)) return 'invalid';
      // | or || → invalid (use 'or' keyword instead)
      if (stream.match(/^\|\|?/)) return 'invalid';

      // Multi-character operators (>=, <=, ==, !=, <<, >>, .*, ./, .^)
      if (stream.match(/^(>=|<=|==|!=|<<|>>|\.\*|\.\^|\.\/)/)) {
        const matched = stream.current();
        if (config.restrictOperators && !expandedAllowedOperators.has(matched)) {
          return 'error disallowedOperator';
        }
        return 'operator';
      }

      // Single-character operators and parentheses
      if (stream.match(/^[+\-*/^%><()&|~?:=]/)) {
        const matched = stream.current();
        if (matched === '(' || matched === ')') {
          return 'paren';
        }
        if (config.restrictOperators && !expandedAllowedOperators.has(matched)) {
          return 'error disallowedOperator';
        }
        return 'operator';
      }

      // Comma (function argument separator)
      if (stream.match(/^,/)) {
        return 'separator';
      }

      // Semicolon (statement separator)
      if (stream.match(/^;/)) {
        return 'separator';
      }

      // String literals (single or double quoted)
      if (stream.match(/^"([^"\\]|\\.)*"/) || stream.match(/^'([^'\\]|\\.)*'/)) {
        return 'string';
      }

      // Square brackets (matrix/index)
      if (stream.match(/^[[\]]/)) {
        return 'paren';
      }

      // Variable names / function names / constants / word operators
      if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_-]*/)) {
        const matched = stream.current();

        // Check if it's a word-based operator (and, or, xor, not, to, in)
        if (WORD_OPERATORS.has(matched)) {
          // 'and', 'or', 'not' may be in allowedFunctions (e.g. NTE config)
          // → do not flag them as disallowed operators; highlight as operator token
          if (allowedFunctions.has(matched)) {
            return 'operator';
          }
          // Conditional-only operators — no error in the highlighter,
          // the validation pass handles the contextual check
          if (inConditionalOnlyOperators.has(matched)) {
            return 'operator';
          }
          if (config.restrictOperators && !expandedAllowedOperators.has(matched)) {
            return 'error disallowedOperator';
          }
          return 'operator';
        }

        // Check if it's a known math constant
        if (ALL_CONSTANTS_SET.has(matched)) {
          if (config.restrictConstants && !allowedConstants.has(matched)) {
            return 'error disallowedConstant';
          }
          return 'constantName';
        }

        // Check if it's a known math function
        if (ALL_FUNCTIONS_SET.has(matched)) {
          if (config.restrictFunctions && !allowedFunctions.has(matched)) {
            return 'error disallowedFunction';
          }
          return 'functionName';
        }

        // Check if it's a known variable
        if (variableNames.has(matched)) {
          return 'variableName';
        }

        return 'unknownVariable';
      }

      // Skip unknown characters
      stream.next();
      return 'error';
    }
  };
}

/**
 * Creates a CodeMirror LanguageSupport for formula syntax with variable highlighting.
 */
export function formulaLanguage(
  variableNames: Set<string>,
  config: FormulaEditorConfig = DEFAULT_FORMULA_EDITOR_CONFIG
): LanguageSupport {
  const parser = formulaStreamParser(variableNames, config);
  return new LanguageSupport(StreamLanguage.define(parser));
}

